import { AlertTriangle, Plus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { fmtINR } from "@/lib/documents";

export const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" }, { value: "card", label: "Card" }, { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" }, { value: "ota", label: "OTA" }, { value: "cheque", label: "Cheque" },
];
export const REFERENCE_REQUIRED = new Set(["card", "upi", "bank_transfer", "cheque"]);
export const methodLabel = (m) => PAYMENT_METHODS.find((x) => x.value === m)?.label || (m || "").replace(/_/g, " ");
export const todayISO = () => new Date().toLocaleDateString("en-CA");
export const emptyAdvance = () => ({ amount: "", method: "card", reference: "", paid_at: todayISO(), note: "Advance at booking" });
export const advanceTotal = (lines) => (lines || []).reduce((s, l) => s + (Number(l.amount) || 0), 0);

/** Returns the first validation problem across the lines, or null. */
export function validateAdvances(lines) {
  for (let i = 0; i < (lines || []).length; i++) {
    const l = lines[i];
    if (!(Number(l.amount) > 0)) return `Payment ${i + 1}: amount must be greater than zero`;
    if (!l.method) return `Payment ${i + 1}: payment method is required`;
    if (REFERENCE_REQUIRED.has(l.method) && !(l.reference || "").trim()) return `Payment ${i + 1}: reference number is required for ${methodLabel(l.method)}`;
    if (!l.paid_at) return `Payment ${i + 1}: payment date is required`;
    if (l.paid_at > todayISO()) return `Payment ${i + 1}: payment date cannot be in the future`;
  }
  return null;
}

/** Serialise lines for the API (advance_payments[] + totals). */
export function advancePayload(lines, grandTotal) {
  const total = advanceTotal(lines);
  return {
    advance_payments: lines.map((l) => ({ amount: Number(l.amount), method: l.method, reference: (l.reference || "").trim() || null,
      paid_at: l.paid_at ? `${l.paid_at}T12:00:00+05:30` : null, note: (l.note || "").trim() || null })),
    total_advance_paid: Math.round(total * 100) / 100,
    balance_at_checkin: Math.round(Math.max(0, (Number(grandTotal) || 0) - total) * 100) / 100,
  };
}

/** Shared "Advance Payment" section — toggle → repeating payment blocks → live balance summary. */
export function AdvancePaymentInput({ value, onChange, grandTotal = 0, nights = 0, alreadyPaid = 0, testidPrefix = "adv", showToggle = true }) {
  const lines = value || [];
  const enabled = !showToggle || lines.length > 0;
  const paid = advanceTotal(lines) + (Number(alreadyPaid) || 0);
  const grand = Number(grandTotal) || 0;
  const balance = Math.max(0, grand - paid);
  const exceeds = grand > 0 && paid > grand + 0.005;
  const set = (i, patch) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const toggle = (on) => onChange(on ? [emptyAdvance()] : []);

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-section`}>
      {showToggle && (
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid={`${testidPrefix}-toggle-row`}>
          <Checkbox checked={enabled} onCheckedChange={(v) => toggle(!!v)} className="mt-0.5 data-[state=checked]:bg-hocp-mint data-[state=checked]:border-hocp-mint data-[state=checked]:text-hocp-deepTeal" data-testid={`${testidPrefix}-toggle`} />
          <span>
            <span className="block text-[13px] font-semibold text-hocp-textPrimary">Record advance payment</span>
            <span className="block text-[12px] text-hocp-textMuted">Guest has made a full or partial payment at booking</span>
          </span>
        </label>
      )}

      {enabled && lines.map((l, i) => (
        <div key={i} className="rounded-md border border-hocp-border bg-white p-3 space-y-3" data-testid={`${testidPrefix}-line-${i}`}>
          <div className="flex items-center justify-between">
            <div className="text-[11px] mono uppercase tracking-wider text-hocp-textMuted">Payment {i + 1}</div>
            {lines.length > 1 && (
              <button type="button" className="text-[12px] text-hocp-error underline" onClick={() => onChange(lines.filter((_, j) => j !== i))} data-testid={`${testidPrefix}-remove-${i}`}>Remove</button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[13px] text-hocp-textMuted">Amount <span className="text-hocp-error">*</span></Label>
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-hocp-textMuted text-[13px]">₹</span>
                <Input type="number" min={0} step="0.01" inputMode="decimal" value={l.amount} placeholder="0.00" className="mono pl-7"
                  onChange={(e) => set(i, { amount: e.target.value })} data-testid={`${testidPrefix}-amount-${i}`} /></div>
            </div>
            <div>
              <Label className="text-[13px] text-hocp-textMuted">Payment Method <span className="text-hocp-error">*</span></Label>
              <Select value={l.method} onValueChange={(v) => set(i, { method: v })}>
                <SelectTrigger data-testid={`${testidPrefix}-method-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[13px] text-hocp-textMuted">Reference No{REFERENCE_REQUIRED.has(l.method) ? <span className="text-hocp-error"> *</span> : null}</Label>
              <Input value={l.reference || ""} placeholder="TXN / UTR / cheque no." onChange={(e) => set(i, { reference: e.target.value })} data-testid={`${testidPrefix}-ref-${i}`} />
              <div className="text-[11px] text-hocp-textTertiary mt-0.5">Required for Card, UPI, Bank Transfer, Cheque</div>
            </div>
            <div>
              <Label className="text-[13px] text-hocp-textMuted">Payment Date <span className="text-hocp-error">*</span></Label>
              <DatePicker value={l.paid_at} onChange={(v) => set(i, { paid_at: v })} maxDate={todayISO()} data-testid={`${testidPrefix}-date-${i}`} />
            </div>
            <div className="col-span-2">
              <Label className="text-[13px] text-hocp-textMuted">Note</Label>
              <Input value={l.note || ""} placeholder="Advance at booking" onChange={(e) => set(i, { note: e.target.value })} data-testid={`${testidPrefix}-note-${i}`} />
            </div>
          </div>
        </div>
      ))}

      {enabled && (
        <button type="button" className="inline-flex items-center gap-1 text-[13px] font-semibold text-hocp-deepTeal underline" onClick={() => onChange([...lines, emptyAdvance()])} data-testid={`${testidPrefix}-add`}>
          <Plus size={14} strokeWidth={1.5} />Add Another Payment
        </button>
      )}

      {enabled && (
        <div className="rounded-[6px] border border-hocp-border bg-hocp-softIce px-4 py-3 text-[13px] space-y-1" data-testid={`${testidPrefix}-summary`}>
          <div className="flex justify-between gap-3"><span className="text-hocp-textMuted">Room Total{nights ? ` (${nights} night${nights === 1 ? "" : "s"})` : ""}:</span><span className="mono" data-testid={`${testidPrefix}-room-total`}>{fmtINR(grand)}</span></div>
          <div className="flex justify-between gap-3"><span className="text-hocp-textMuted">Advance Paid:</span><span className="mono" data-testid={`${testidPrefix}-paid`}>− {fmtINR(paid)}</span></div>
          <div className="border-t border-hocp-border pt-1 flex justify-between gap-3 font-bold text-hocp-deepTeal"><span>Balance Due at Check-in:</span><span className="mono" data-testid={`${testidPrefix}-balance`}>{fmtINR(balance)}</span></div>
          {exceeds && (
            <div className="mt-1 inline-flex items-center gap-1.5 rounded px-2 py-1 text-[12px] font-medium bg-[#FEF3C7] text-[#78530E]" data-testid={`${testidPrefix}-exceeds`}>
              <AlertTriangle size={13} strokeWidth={1.75} />Advance amount exceeds total room charges
            </div>
          )}
        </div>
      )}
    </div>
  );
}
